/**
 * Measures how often browser extensions (coupon / price-compare overlays,
 * adware) paint their own UI over whoppah.com. Read-only: nothing is hidden
 * or removed, we only count and label what we find so GA4 can tell us the
 * real share of sessions and whether they convert worse.
 *
 * How a foreign node is told apart from ours: extension content scripts run
 * in an isolated JS world, so expando properties they set on DOM nodes are
 * invisible to the page. Every element our React tree renders (portals
 * included) carries a `__reactFiber$…` / `__reactContainer$…` key in the page
 * world; an injected overlay does not, even when the extension itself uses
 * React. Page-world third parties (GTM, Statsig, …) are not React either, so
 * a signature is only counted as `strong` when it has a tell no page script
 * of ours produces: a custom-element tag, a shadow root, an extension URL, or
 * a node outside <body>.
 */

export const EXTENSION_INJECTION_EVENT = "extension_injection";

const IGNORED_TAGS = new Set([
  "SCRIPT",
  "STYLE",
  "LINK",
  "NOSCRIPT",
  "TEMPLATE",
  "META",
  "NEXT-ROUTE-ANNOUNCER",
]);

const EXTENSION_URL = /^(chrome|moz|safari-web|ms-browser)-extension:\/\//i;

/* Labels for the extensions we expect most; anything else still reports its
   raw signature, so this list only makes the GA4 report easier to read. */
const KNOWN_EXTENSIONS: ReadonlyArray<[string, RegExp]> = [
  ["honey", /honey/i],
  ["capital_one_shopping", /wikibuy|capital.?one|c1-?shopping/i],
  ["rakuten", /rakuten|ebates/i],
  ["klarna", /klarna/i],
  ["karma", /karma/i],
  ["coupert", /coupert/i],
  ["cently", /cently|couponfollow/i],
  ["temu", /temu/i],
  ["aliexpress", /aliexpress|ali-?helper/i],
  ["shopback", /shopback/i],
  ["price_compare_generic", /price-?compare|better-?deals/i],
];

export type InjectionScan = {
  strong: boolean;
  signatures: string[];
  known: string[];
  extensionUrls: number;
};

const hasReactKey = (el: Element) =>
  Object.keys(el).some(
    key =>
      key.startsWith("__reactFiber$") || key.startsWith("__reactContainer$")
  );

const signatureOf = (el: Element) => {
  const id = el.id ? `#${el.id}` : "";
  const cls =
    typeof el.className === "string" && el.className.trim()
      ? `.${el.className.trim().split(/\s+/).slice(0, 2).join(".")}`
      : "";
  return `${el.tagName.toLowerCase()}${id}${cls}`.slice(0, 60);
};

const isStrong = (el: Element, outsideBody: boolean) =>
  outsideBody ||
  el.tagName.includes("-") ||
  Boolean(el.shadowRoot) ||
  EXTENSION_URL.test(el.getAttribute("src") ?? "");

export const scanForInjectedElements = (doc: Document): InjectionScan => {
  const candidates: Array<{ el: Element; outsideBody: boolean }> = [];

  Array.from(doc.documentElement.children).forEach(el => {
    if (el !== doc.head && el !== doc.body && !IGNORED_TAGS.has(el.tagName)) {
      candidates.push({ el, outsideBody: true });
    }
  });

  Array.from(doc.body?.children ?? []).forEach(el => {
    if (el.id === "__next" || IGNORED_TAGS.has(el.tagName) || hasReactKey(el)) {
      return;
    }

    candidates.push({ el, outsideBody: false });
  });

  const extensionUrls = Array.from(doc.querySelectorAll("[src],[href]")).filter(
    el =>
      EXTENSION_URL.test(
        el.getAttribute("src") ?? el.getAttribute("href") ?? ""
      )
  ).length;

  const signatures = Array.from(
    new Set(candidates.map(candidate => signatureOf(candidate.el)))
  );
  const haystack = signatures.join(" ");
  const known = KNOWN_EXTENSIONS.filter(([, pattern]) =>
    pattern.test(haystack)
  ).map(([label]) => label);

  return {
    strong:
      extensionUrls > 0 ||
      candidates.some(candidate =>
        isStrong(candidate.el, candidate.outsideBody)
      ),
    signatures,
    known,
    extensionUrls,
  };
};

/* GA4 caps string parameters at 100 characters. */
export const toEventParams = (scan: InjectionScan) => ({
  ext_strong: scan.strong,
  ext_known: scan.known.join(",").slice(0, 100) || "(none)",
  ext_signatures: scan.signatures.join(" ").slice(0, 100),
  ext_url_count: scan.extensionUrls,
  ext_node_count: scan.signatures.length,
});

export const shouldReport = (scan: InjectionScan) =>
  scan.signatures.length > 0 || scan.extensionUrls > 0;
